import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, Maximize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageTransition from '../components/Motion/PageTransition';

import plGif from '../projectScreenshots/P&L/P&L.gif';
import bsGif from '../projectScreenshots/Balance Sheet/BalanceSheet.gif';
import eoGif from '../projectScreenshots/Exact Online/ExactOnlineGIF.gif';
import nvsGif from '../projectScreenshots/NVS/NVS.gif';
import plOwnershipGif from '../projectScreenshots/Profit and Loss with Company Ownership/New P&L.gif';
import tmsGif from '../projectScreenshots/TMS/New TMS.gif';
import bouwsoftGif from '../projectScreenshots/Bouwsoft Order and Project Managent Dashboard/Bouwsoft.gif';

/**
 * FullscreenImageViewer - Modal for viewing images in fullscreen with navigation
 */
function FullscreenImageViewer({ images, currentIndex, onClose, onNavigate }) {
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') onClose();
      if (images.length > 1 && e.key === 'ArrowLeft') onNavigate('prev');
      if (images.length > 1 && e.key === 'ArrowRight') onNavigate('next');
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onClose, onNavigate, images.length]);

  return (
    <motion.div
      className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
        aria-label="Close viewer"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Image counter - only show for multiple images */}
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-white text-lg font-medium">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Previous button - only show for multiple images */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate('prev');
          }}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      {/* Image */}
      <motion.img
        key={currentIndex}
        src={images[currentIndex]}
        alt={`Screenshot ${currentIndex + 1}`}
        className="max-w-[90vw] max-h-[90vh] object-contain"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Next button - only show for multiple images */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate('next');
          }}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all"
          aria-label="Next image"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Instructions - only show for multiple images */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white/70 text-sm">
          Use arrow keys or click buttons to navigate • Press ESC to close
        </div>
      )}

      {/* Instructions for single image */}
      {images.length === 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white/70 text-sm">
          Press ESC or click outside to close
        </div>
      )}
    </motion.div>
  );
}

/**
 * ProjectCard - Interactive project showcase with image carousel on hover
 */
function ProjectCard({ project, index }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [intervalId, setIntervalId] = useState(null);

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        delay: index * 0.15,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  // Auto-cycle images on hover (slower: 2.5 seconds per image)
  // Disabled for projects with disableHover flag or single image
  const handleMouseEnter = () => {
    if (project.disableHover || !project.images || project.images.length <= 1) return;

    setIsHovered(true);
    const id = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
    }, 2500); // Slower: 2.5 seconds per image
    setIntervalId(id);
  };

  const handleMouseLeave = () => {
    if (project.disableHover || !project.images || project.images.length <= 1) return;

    setIsHovered(false);
    setCurrentImageIndex(0);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  const handleFullscreenNavigate = (direction) => {
    if (direction === 'prev') {
      setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
    } else {
      setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
    }
  };

  return (
    <>
      <motion.article
        className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 group"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        whileHover={{ y: -8 }}
      >
        {/* Image Carousel */}
        <div className={`relative bg-gray-900 overflow-hidden ${project.isWidescreen ? 'aspect-video' : 'h-80'}`}>
          <AnimatePresence mode="wait">
            <motion.img
              key={currentImageIndex}
              src={project.images[currentImageIndex]}
              alt={`${project.title} - Screenshot ${currentImageIndex + 1}`}
              className="w-full h-full object-contain cursor-pointer"
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => setShowFullscreen(true)}
            />
          </AnimatePresence>

          {/* Hover hint - only show if hover preview is enabled */}
          {!isHovered && !project.disableHover && project.images && project.images.length > 1 && (
            <motion.div
              className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-xs"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Hover to preview
            </motion.div>
          )}

          {/* Fullscreen button */}
          {!project.disableFullscreen && (
            <button
              onClick={() => setShowFullscreen(true)}
              className="absolute bottom-4 right-4 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white p-2 rounded-lg transition-all flex items-center gap-2 text-xs font-medium"
              aria-label="View fullscreen"
            >
              <Maximize2 className="w-4 h-4" />
              <span>View Full</span>
            </button>
          )}

          {/* Image counter - only show if there are multiple images */}
          {project.images && project.images.length > 1 && (
            <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-sm font-medium">
              {currentImageIndex + 1} / {project.images.length}
            </div>
          )}
        </div>
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
            {project.title}
          </h2>
          <p className="text-gray-600 mb-4 leading-relaxed">
            {project.description}
          </p>

          {/* Key Features */}
          {project.features && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Key Features
              </h3>
              <ul className="space-y-1">
                {project.features.map((feature, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {project.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
    </motion.article>

    {/* Fullscreen Image Viewer */}
    <AnimatePresence>
      {showFullscreen && (
        <FullscreenImageViewer
          images={project.fullscreenImages || project.images}
          currentIndex={currentImageIndex}
          onClose={() => setShowFullscreen(false)}
          onNavigate={handleFullscreenNavigate}
        />
      )}
    </AnimatePresence>
    </>
  );
}

/**
 * Projects Page - Real project showcase with interactive image previews
 * Respects prefers-reduced-motion via Framer Motion's built-in support
 */
function Projects() {
  const navigate = useNavigate();

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const projects = [
    {
      id: 'exact',
      title: 'Exact Online - Financial Performance Analytics',
      description: 'ERP analytics suite for Exact Online with dashboard and matrix views across revenue, EBITDA, overhead, and net results, plus multi-year comparisons and drill-downs.',
      features: [
        'Dashboard and matrix navigation for quick context switching',
        'KPI tiles for EBITDA, overhead, and net results',
        'Revenue and COGS evolution with multi-year comparisons',
        'Cumulative revenue and COGS by year',
        'Bridge and waterfall-style variance analysis'
      ],
      tags: ['Exact Online', 'ERP Analytics', 'Revenue & COGS', 'Matrix Reporting', 'Executive KPIs'],
      images: [eoGif],
      isWidescreen: true
    },
    {
      id: 'nvs',
      title: 'Dynamics NAV/365 BC - Warehouse Operations Analytics',
      description: 'Warehouse performance dashboards for Dynamics NAV/365 Business Central with transaction, labor, and order metrics, plus descriptive analysis to spotlight operational risks.',
      features: [
        'Operational KPIs for invoice amount, labor cost, transactions, hours, and rates',
        'Order weight and volume analytics with monthly trends',
        'Transaction type breakdowns and cost comparisons',
        'Delay and efficiency indicators across warehouse processes',
        'Descriptive analysis panels with summary conclusions'
      ],
      tags: ['Dynamics NAV', 'Dynamics 365 BC', 'Warehouse Analytics', 'WMS', 'Operations'],
      images: [nvsGif],
      isWidescreen: true
    },
    {
      id: 'pl',
      title: 'P&L - Profit & Loss Executive Dashboard',
      description: 'Executive P&L dashboard with KPI cards, geographic revenue view, and narrative insights for fast profitability decisions.',
      features: [
        'KPI cards for revenue, COGS, expenses, taxes, and profit/loss',
        'Revenue by location map and monthly income trends',
        'YoY growth and period comparison visuals',
        'Profit margin, expense ratio, and tax rate indicators',
        'Insight panel with recommendations and action cues'
      ],
      tags: ['P&L', 'Financial Analytics', 'Executive Dashboard', 'KPI Tracking', 'Revenue Insights'],
      images: [plGif],
      isWidescreen: true
    },
    {
      id: 'bs',
      title: 'Balance Sheet - Financial Position & Liquidity',
      description: 'Balance sheet analytics with asset/liability/equity KPIs, liquidity ratios, and composition views to track financial position over time.',
      features: [
        'Assets, liabilities, equity, and working capital KPI cards',
        'Cash, quick, and current ratio indicators',
        'Assets and equity composition charts',
        'YoY balance sheet trend comparisons',
        'Filterable tables with period and entity selectors'
      ],
      tags: ['Balance Sheet', 'Liquidity', 'Financial Position', 'Ratio Analysis', 'Drill-down'],
      images: [bsGif],
      isWidescreen: true
    },
    {
      id: 'pl-ownership',
      title: 'Profit & Loss with Company Ownership Structure',
      description: 'Financial reporting workspace combining P&L/Balance views with ownership-structure visualization for multi-entity groups.',
      features: [
        'Balance sheet vs income statement toggle',
        'Consolidated and statutory reporting views',
        'Book value vs market value basis selection',
        'Year and month period filters',
        'Interactive ownership tree with percentage stakes and summaries'
      ],
      tags: ['Ownership Structure', 'Consolidation', 'Financial Reporting', 'Corporate Groups', 'P&L'],
      images: [plOwnershipGif],
      isWidescreen: true
    },
    {
      id: 'tms',
      title: 'TMS - Transportation Performance Dashboard',
      description: 'Transportation management analytics covering revenue, cost, on-time performance, and delay insights across trips and assignments.',
      features: [
        'Revenue, cost, and gross profit KPIs',
        'On-time assignment vs planned performance tracking',
        'Waiting and idle hours trends',
        'Late-route distribution with lateness buckets',
        'Shipment-level table with assignment delays'
      ],
      tags: ['TMS', 'Transportation', 'On-Time Performance', 'Fleet Analytics', 'Delay Analysis'],
      images: [tmsGif],
      isWidescreen: true
    },
    {
      id: 'bouwsoft',
      title: 'Bouwsoft - Orders & Projects Management Analytics',
      description: 'Bouwsoft analytics suite with orders and project dashboards, combining financial KPIs with project sales, cost, and machine-hour insights.',
      features: [
        'Orders and projects reporting hub',
        'Revenue, cost, VAT, orders, and net KPIs with YoY trends',
        'Order status analysis and operational summaries',
        'Project sales, cost, margin, and purchase tabs',
        'Machine hours, project bridge, and cost distribution views'
      ],
      tags: ['Bouwsoft', 'Construction ERP', 'Order Management', 'Project Analytics', 'Profitability'],
      images: [bouwsoftGif],
      isWidescreen: true
    }
  ];

  return (
    <PageTransition>
      <div className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center mb-16"
            initial="hidden"
            animate="visible"
            variants={headerVariants}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Our Projects
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Explore our portfolio of data analytics and business intelligence solutions.
              Each project showcases our expertise in transforming complex data into actionable insights.
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto space-y-8">
            {projects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            className="max-w-3xl mx-auto text-center mt-16 p-8 bg-blue-600 rounded-2xl text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Data?</h2>
            <p className="text-blue-100 mb-6">
              Let's discuss how we can create a custom analytics solution for your business
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/live-dashboard')}
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                View Live Action
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Get in Touch
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </PageTransition>
  );
}

export default Projects;